import { useEffect, useMemo, useState } from 'react';
import { ClipboardList, Plus, Trash2 } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { buildTaskDispatchCard } from '../../core/dispatch-card';
import { ANY } from '../../core/fleet-filters';
import { teamsOfTask, visibleTeamsFor } from '../../core/task-views';
import {
  EMPTY_TASK_FILTERS,
  filterTasks,
  redactTasks,
  taskPriorityCounts,
  taskSummary,
} from '../../core/task-filters';
import type { TaskFilters } from '../../core/task-filters';
import { hasPermission } from '../../core/permissions';
import { readOpenScreen, writeOpenScreen } from '../../core/session-cache';
import type { OpenScreen } from '../../core/session-cache';
import { deleteTask } from '../../db/task-store';
import { TASK_PRIORITIES, TASK_PRIORITY_LABELS } from '../../types/task';
import type { Task, TaskType } from '../../types/task';
import type { Contact } from '../../types/contact';
import type { Soldier, Vehicle } from '../../types/fleet';
import type { User } from '../../types/user';
import { CreateTaskModal } from './CreateTaskModal';
import { RecycleBinModal } from './RecycleBinModal';
import { TaskCard } from './TaskCard';
import { TaskDetailRouter } from './TaskDetailRouter';
import { TaskFilterBar } from './TaskFilterBar';
import { useTaskTeams } from './use-task-teams';
import { TaskTeamsModal } from './assignment';

interface TasksPageProps {
  user: User;
  tasks: readonly Task[];
  taskTypes: readonly TaskType[];
  contacts: readonly Contact[];
  /** The fleet the task's vehicle teams are built from. */
  vehicles: readonly Vehicle[];
  soldiers: readonly Soldier[];
  loading: boolean;
  reload: () => Promise<void>;
  /** Set by the dashboard shortcut "פתיחת משימה חדשה" — opens the form once. */
  requestCreate?: boolean;
  onCreateRequestHandled?: () => void;
}

/** Which task the form is open for: an existing record, or a blank one. */
type FormTarget = { mode: 'create' } | { mode: 'edit'; task: Task };

/**
 * The dialogs whose being-open survives a reload. A pull-to-refresh in the
 * middle of a task used to close everything and drop the operator back on the
 * board; the snapshot in `src/core/session-cache.ts` puts them back where they
 * were, and a `kind` an older build wrote is simply ignored.
 */
const OPEN_SCREEN_KINDS = ['create', 'edit', 'detail', 'teams', 'bin'] as const;

/** Tab strip: every priority, plus the catch-all. */
const PRIORITY_TABS: readonly TaskFilters['priority'][] = [ANY, ...TASK_PRIORITIES];

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-panel border border-line rounded-2xl p-3 text-center">
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      <p className="text-xs text-ink/50">{label}</p>
    </div>
  );
}

/** The tasks board: counters, priority tabs, search and the card grid. */
export function TasksPage({
  user,
  tasks,
  taskTypes,
  contacts,
  vehicles,
  soldiers,
  loading,
  reload,
  requestCreate = false,
  onCreateRequestHandled,
}: TasksPageProps) {
  const [filters, setFilters] = useState<TaskFilters>(EMPTY_TASK_FILTERS);
  const [form, setForm] = useState<FormTarget | null>(null);
  /** The task whose vehicle teams are open in the assignment dialog. */
  const [teamsTask, setTeamsTask] = useState<Task | null>(null);
  /** The task whose role-based detail view is open — kept by id, so a reload
   *  never leaves the screen showing a stale copy. */
  const [detailTaskId, setDetailTaskId] = useState<string | null>(null);
  /** The task the delete confirmation is open for. */
  const [pendingDelete, setPendingDelete] = useState<Task | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [binOpen, setBinOpen] = useState(false);
  /** What was open when this page was last left — consumed once, then cleared. */
  const [pendingScreen, setPendingScreen] = useState<OpenScreen | null>(() =>
    readOpenScreen(OPEN_SCREEN_KINDS),
  );
  /** Fixed evaluation moment, so "היום" does not drift between renders. */
  const [now] = useState(() => Date.now());

  const canCreate = hasPermission(user, 'CREATE_TASK');
  const canEdit = hasPermission(user, 'EDIT_TASK');
  // The same permission opens the bin: whoever may delete a task may put it
  // back, and may empty the bin for good.
  const canDelete = hasPermission(user, 'DELETE_TASK');
  // Without ASSIGN_TASK the convoy is not reachable from the board at all — a
  // soldier reaches only their own vehicle, through the field view.
  const canAssign = hasPermission(user, 'ASSIGN_TASK');

  // Internal manager notes are stripped before the board ever renders them.
  const readable = useMemo(() => redactTasks(user, tasks), [user, tasks]);
  const summary = useMemo(() => taskSummary(readable, now), [readable, now]);
  const priorityCounts = useMemo(() => taskPriorityCounts(readable), [readable]);
  const visible = useMemo(
    () => filterTasks(readable, filters, { contacts, taskTypes, now }),
    [readable, filters, contacts, taskTypes, now],
  );

  const detailTask = detailTaskId ? readable.find((task) => task.id === detailTaskId) ?? null : null;

  // The convoys of the tasks on screen, for the portrait dispatch order every
  // card can share. One round-trip for the whole board, exactly as the dispatch
  // sheet loads it.
  // `useTaskTeams` re-reads off the reactive store, so this list is the convoy
  // the database holds *now* — not the one the board loaded on mount. The
  // dispatch cards below are derived from it, which is what lets a manager
  // assign a convoy and share its image in the same breath.
  const visibleIds = useMemo(() => visible.map((task) => task.id), [visible]);
  const { teams, reload: reloadTeams } = useTaskTeams(visibleIds);

  /** One shareable order per task, scoped to the crew this account may read. */
  const dispatchCards = useMemo(() => {
    const cards = new Map<string, ReturnType<typeof buildTaskDispatchCard>>();
    for (const task of visible) {
      const taskTeams = teamsOfTask(teams, task.id);
      cards.set(
        task.id,
        buildTaskDispatchCard(task, {
          teams: visibleTeamsFor(user, { teams: taskTeams, soldiers }),
          vehicles,
          soldiers,
          contacts,
          taskTypes,
          now,
        }),
      );
    }
    return cards;
  }, [visible, teams, user, vehicles, soldiers, contacts, taskTypes, now]);

  // The dialog the operator had open before the refresh, re-opened once the
  // board has its records back — a task id is only worth anything against the
  // tasks it names, and a dialog the account may not open is dropped.
  useEffect(() => {
    if (!pendingScreen || loading) return;
    const target = pendingScreen.id
      ? readable.find((task) => task.id === pendingScreen.id) ?? null
      : null;
    if (pendingScreen.kind === 'create' && canCreate) setForm({ mode: 'create' });
    else if (pendingScreen.kind === 'edit' && target && canEdit) setForm({ mode: 'edit', task: target });
    else if (pendingScreen.kind === 'detail' && target) setDetailTaskId(target.id);
    else if (pendingScreen.kind === 'teams' && target && canAssign) setTeamsTask(target);
    else if (pendingScreen.kind === 'bin' && canDelete) setBinOpen(true);
    setPendingScreen(null);
  }, [pendingScreen, loading, readable, canCreate, canEdit, canAssign, canDelete]);

  // …and mirrored again on every change, so the next reload has something to
  // restore. Written only once the pending snapshot has been consumed, or the
  // first render would erase it before it was read.
  useEffect(() => {
    if (pendingScreen) return;
    const screen: OpenScreen | null =
      form?.mode === 'create'
        ? { kind: 'create', id: '' }
        : form?.mode === 'edit'
          ? { kind: 'edit', id: form.task.id }
          : teamsTask
            ? { kind: 'teams', id: teamsTask.id }
            : binOpen
              ? { kind: 'bin', id: '' }
              : detailTaskId
                ? { kind: 'detail', id: detailTaskId }
                : null;
    writeOpenScreen(screen);
  }, [pendingScreen, form, teamsTask, binOpen, detailTaskId]);

  // A shortcut from the command dashboard opens the board's own form — the
  // dashboard never carries a second copy of it.
  useEffect(() => {
    if (!requestCreate) return;
    if (canCreate) {
      setDetailTaskId(null);
      setForm({ mode: 'create' });
    }
    onCreateRequestHandled?.();
  }, [requestCreate, canCreate, onCreateRequestHandled]);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteTask(user, pendingDelete.id);
      // A task that was open on screen is no longer on the board.
      if (detailTaskId === pendingDelete.id) setDetailTaskId(null);
      setPendingDelete(null);
      await reload();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  // The dialogs stay mounted for both screens: the manager's detail view
  // reaches the very same task form, assignment dialog and delete confirmation
  // the board offers.
  const dialogs = (
    <>
      {form ? (
        <CreateTaskModal
          user={user}
          task={form.mode === 'edit' ? form.task : null}
          taskTypes={taskTypes}
          contacts={contacts}
          vehicles={vehicles}
          soldiers={soldiers}
          onClose={() => setForm(null)}
          onSaved={reload}
        />
      ) : null}

      {teamsTask ? (
        <TaskTeamsModal
          user={user}
          task={teamsTask}
          vehicles={vehicles}
          soldiers={soldiers}
          onSaved={reloadTeams}
          onClose={() => {
            setTeamsTask(null);
            // The convoy is re-read on the way out as well, so the order the
            // board is about to share is the one that was just assigned.
            void reloadTeams();
          }}
        />
      ) : null}

      {pendingDelete ? (
        <Modal
          title="מחיקת משימה"
          subtitle={pendingDelete.title}
          onClose={() => {
            setPendingDelete(null);
            setDeleteError(null);
          }}
          footer={
            <>
              <button
                type="button"
                onClick={() => {
                  setPendingDelete(null);
                  setDeleteError(null);
                }}
                className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
              >
                ביטול
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => void handleDelete()}
                className="h-10 px-5 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-40"
              >
                {deleting ? 'מוחק…' : 'מחק'}
              </button>
            </>
          }
        >
          <p className="text-sm">האם אתה בטוח שברצונך למחוק משימה זו?</p>
          <p className="mt-2 text-xs text-ink/50">
            המשימה תעבור לסל המחזור וניתן יהיה לשחזר אותה במשך 30 ימים.
          </p>
          {deleteError ? (
            <p className="mt-3 text-sm rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3">
              {deleteError}
            </p>
          ) : null}
        </Modal>
      ) : null}

      {binOpen ? (
        <RecycleBinModal
          user={user}
          taskTypes={taskTypes}
          now={now}
          onClose={() => setBinOpen(false)}
          onChanged={reload}
        />
      ) : null}
    </>
  );

  // Opening a task hands it to the router, which decides between the manager
  // dashboard, the soldier's field view and the refusal screen.
  if (detailTask) {
    return (
      <>
        <TaskDetailRouter
          user={user}
          task={detailTask}
          taskTypes={taskTypes}
          contacts={contacts}
          vehicles={vehicles}
          soldiers={soldiers}
          onBack={() => setDetailTaskId(null)}
          onEdit={canEdit ? (target) => setForm({ mode: 'edit', task: target }) : undefined}
          onManageTeams={canAssign ? (target) => setTeamsTask(target) : undefined}
          onDelete={canDelete ? (target) => setPendingDelete(target) : undefined}
          onReload={reload}
        />
        {dialogs}
      </>
    );
  }

  return (
    // The board is held to the same frame the command dashboard uses — one
    // width for the whole application — and clipped at it. `min-w-0` is the
    // part that actually matters: without it a single wide child (a long
    // destination, a plate tag, a filter strip) grows this column past the
    // viewport and the *page* scrolls sideways on a phone.
    <div className="mx-auto w-full min-w-0 max-w-6xl box-border overflow-x-clip space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <ClipboardList size={20} className="text-brand-500" aria-hidden />
          משימות
        </h2>
        <div className="flex items-center gap-2">
          {canDelete ? (
            <button
              type="button"
              onClick={() => setBinOpen(true)}
              className="h-10 px-4 rounded-xl border border-line text-sm text-ink/70 hover:text-ink flex items-center gap-2"
            >
              <Trash2 size={16} aria-hidden />
              סל מחזור
            </button>
          ) : null}
          {canCreate ? (
            <button
              type="button"
              onClick={() => setForm({ mode: 'create' })}
              className="h-10 px-4 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 flex items-center gap-2"
            >
              <Plus size={16} aria-hidden />
              משימה חדשה
            </button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile label="סה״כ משימות" value={summary.all} />
        <StatTile label="להיום" value={summary.today} />
        <StatTile label="פתוחות" value={summary.open} />
        <StatTile label="דחופות" value={summary.urgent} />
      </div>

      {/* Wrapped, not scrolled: a sideways strip on a phone either hides the
          last tab or drags the whole page with it. Five short tabs fit two
          rows on the narrowest screen the unit carries. */}
      <nav aria-label="סינון לפי דחיפות" className="flex flex-wrap gap-1.5 pb-1">
        {PRIORITY_TABS.map((priority) => {
          const isActive = filters.priority === priority;
          const label = priority === ANY ? 'הכול' : TASK_PRIORITY_LABELS[priority];
          return (
            <button
              key={priority}
              type="button"
              onClick={() => setFilters({ ...filters, priority })}
              aria-current={isActive ? 'true' : undefined}
              className={[
                'shrink-0 h-9 px-3 rounded-xl border text-sm transition-colors',
                isActive
                  ? 'border-brand-500 bg-brand-50 text-brand-700 font-semibold'
                  : 'border-line bg-panel text-ink/60 hover:text-ink',
              ].join(' ')}
            >
              {label}
              <span className="mr-1.5 text-xs text-ink/40 tabular-nums">
                {priorityCounts[priority]}
              </span>
            </button>
          );
        })}
      </nav>

      <TaskFilterBar
        filters={filters}
        taskTypes={taskTypes}
        onChange={setFilters}
        resultCount={visible.length}
      />

      {loading ? (
        <p className="text-sm text-ink/50 py-8 text-center">טוען את לוח המשימות…</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-ink/50 py-8 text-center">
          לא נמצאו משימות התואמות את הסינון{filters.text ? ` "${filters.text}"` : ''}.
        </p>
      ) : (
        // A grid item is `min-width: auto` by default — one unbreakable line
        // inside a card would widen its whole column. Every cell is pinned to
        // zero instead, so a card is never wider than the track it sits in.
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 [&>*]:min-w-0">
          {visible.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              taskTypes={taskTypes}
              contacts={contacts}
              now={now}
              onOpenDetail={(target) => setDetailTaskId(target.id)}
              onOpen={canEdit ? (target) => setForm({ mode: 'edit', task: target }) : undefined}
              onManageTeams={canAssign ? (target) => setTeamsTask(target) : undefined}
              onDelete={canDelete ? (target) => setPendingDelete(target) : undefined}
              dispatchCard={dispatchCards.get(task.id) ?? null}
            />
          ))}
        </div>
      )}

      {dialogs}
    </div>
  );
}
